import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from './ui/dropdown-menu';
import { BookOpen, ClipboardCheck, ListTodo, LayoutDashboard, LogOut, User, ChevronDown, Trophy, FileText } from 'lucide-react';
import NotificationBell from './NotificationBell';

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const staffNavItems = [
    { icon: LayoutDashboard, label: 'Home', path: isAdmin ? '/admin' : '/dashboard' },
    { icon: BookOpen, label: 'Train', path: '/dashboard' },
    { icon: Trophy, label: 'Ranks', path: '/leaderboard' },
    { icon: ListTodo, label: 'Tasks', path: '/dashboard' },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9]" style={{ fontFamily: "'Manrope', sans-serif" }}>
      {/* Top Navigation */}
      <header className="sticky top-0 z-50 bg-white border-b border-stone-200 shadow-sm" data-testid="app-header">
        <div className="max-w-6xl mx-auto px-4 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')} data-testid="logo-link">
            <img src="/grillsmith-logo-sm.png" alt="GrillSmith" className="h-16 w-auto object-contain" />
          </div>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 rounded-xl text-stone-700 hover:bg-stone-100" data-testid="user-menu-trigger">
                  <div className="w-7 h-7 rounded-full bg-[#1B2A4A]/10 flex items-center justify-center">
                    <User className="w-4 h-4 text-[#1B2A4A]" />
                  </div>
                  <span className="hidden sm:block text-sm font-medium">{user?.name}</span>
                  <ChevronDown className="w-3.5 h-3.5 text-stone-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-xl w-48" data-testid="user-menu">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-stone-900">{user?.name}</p>
                  <p className="text-xs text-stone-500">{user?.email}</p>
                  <p className="text-xs text-[#1B2A4A] font-medium capitalize mt-0.5">{user?.role}</p>
                </div>
                <DropdownMenuSeparator />
                {isAdmin && (
                  <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer" data-testid="go-admin-dashboard">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Admin Dashboard
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate('/dashboard')} className="cursor-pointer" data-testid="go-my-training">
                  <BookOpen className="w-4 h-4 mr-2" /> My Training
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/my-documents')} className="cursor-pointer" data-testid="go-my-documents">
                  <FileText className="w-4 h-4 mr-2" /> My Documents
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/leaderboard')} className="cursor-pointer" data-testid="go-leaderboard">
                  <Trophy className="w-4 h-4 mr-2" /> Leaderboard
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-6">
        {children}
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-stone-200 md:hidden z-50" data-testid="mobile-nav">
        <div className="flex items-center justify-around h-16">
          {staffNavItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.label}
                onClick={() => navigate(item.path)}
                className={`flex flex-col items-center gap-0.5 px-3 py-1 ${isActive ? 'text-[#1B2A4A]' : 'text-stone-400'}`}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-[10px] font-medium">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
